import { useCustomSelectState } from "./useCustomSelectState";
import { ChevronDownIcon } from "../icons";

interface Option {
  readonly value: string;
  readonly label: string;
  readonly dot?: string;
}

interface CustomSelectProps {
  readonly options: readonly Option[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
  readonly footer?: React.ReactNode;
  readonly dropUp?: boolean;
}

export function CustomSelect({
  options,
  value,
  onChange,
  placeholder,
  className = "",
  footer,
  dropUp: forceDropUp,
}: CustomSelectProps) {
  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    ref,
    listRef,
    openDropdown,
    handleKeyDown,
  } = useCustomSelectState({ options, value, onChange });

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? placeholder ?? "";
  const selectedDot = selected?.dot;

  return (
    <div
      ref={ref}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs rounded-md
          bg-surface border border-border/60 text-ink hover:border-accent/50
          focus:outline-none focus:border-accent/60 transition-colors`}
      >
        <span className={`flex items-center gap-1.5 ${value ? "text-ink" : "text-ink-muted"}`}>
          {selectedDot && <span className={`w-2 h-2 rounded-full shrink-0 ${selectedDot}`} />}
          {selectedLabel}
        </span>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-ink-faint transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          className={`absolute z-[9999] w-full max-w-[calc(100vw-1rem)] bg-surface border border-border/60 rounded-xl shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 max-h-60 overflow-y-auto ${
            forceDropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors
                ${option.value === value ? "text-accent bg-accent/10" : "text-ink"}
                ${index === highlightedIndex ? "bg-surface-alt" : ""}`}
            >
              <span className="flex items-center gap-1.5">
                {option.dot && <span className={`w-2 h-2 rounded-full shrink-0 ${option.dot}`} />}
                {option.label}
              </span>
            </button>
          ))}
          {footer}
        </div>
      )}
    </div>
  );
}
